import { Set } from 'immutable'
import React from 'react'
import { StandardLocalWidget, Updater, fromJSX } from 'widgets-for-react'
import { PaginationState } from '../../pagination/paginationState'
import { SearchTab } from '../../search/searchState'
import { ViewListenerState } from '../../viewListener/viewListenerState'
import { Filter } from '../filtersLoader/filtersLoaderState'
import { CategoriesFilter, Filters, TopFilters } from '../filtersUtils'
import { TopFiltersLayout } from './topFiltersLayout'
import {
  ModalState,
  TopFilterState,
  TopFiltersState,
  topFiltersStateUpdaters,
} from './topFiltersState'

interface FilteredUrl {
  (filters: Filters): string
}

export const topFiltersWidget =
  (
    techniques: Filter[],
    appearances: Filter[],
    textures: Filter[],
    allergens: Filter[],
    types: Filter[],
    categoriesFilter: CategoriesFilter,
    topFilters: TopFilters,
    viewListenerState: ViewListenerState,
    paginationState: PaginationState,
    filteredUrl: FilteredUrl,
    term?: string,
    searchTab?: SearchTab
  ): StandardLocalWidget<TopFiltersState> =>
  (state) =>
    fromJSX((setState) => (
      <TopFiltersLayout
        techniques={toTopFilter(
          categoriesFilter,
          techniques,
          state.techniques,
          topFilters.techniques,
          (items: string[]) => ({ ...topFilters, techniques: items }),
          setState,
          topFiltersStateUpdaters.techniquesUpdater,
          filteredUrl,
          term,
          searchTab
        )}
        appearances={toTopFilter(
          categoriesFilter,
          appearances,
          state.appearances,
          topFilters.appearances,
          (items: string[]) => ({ ...topFilters, appearances: items }),
          setState,
          topFiltersStateUpdaters.appearancesUpdater,
          filteredUrl,
          term,
          searchTab
        )}
        textures={toTopFilter(
          categoriesFilter,
          textures,
          state.textures,
          topFilters.textures,
          (items: string[]) => ({ ...topFilters, textures: items }),
          setState,
          topFiltersStateUpdaters.texturesUpdater,
          filteredUrl,
          term,
          searchTab
        )}
        allergens={toTopFilter(
          categoriesFilter,
          allergens,
          state.allergens,
          topFilters.allergens,
          (items: string[]) => ({ ...topFilters, allergens: items }),
          setState,
          topFiltersStateUpdaters.allergensUpdater,
          filteredUrl,
          term,
          searchTab
        )}
        types={toTopFilter(
          categoriesFilter,
          types,
          state.types,
          topFilters.types,
          (items: string[]) => ({ ...topFilters, types: items }),
          setState,
          topFiltersStateUpdaters.typesUpdater,
          filteredUrl,
          term,
          searchTab
        )}
        view={viewListenerState}
        resetAll={{
          url: filteredUrl({
            term: term,
            searchTab: searchTab,
            categoriesFilter: categoriesFilter,
          }),
          hide:
            (!topFilters.allergens || topFilters.allergens.length < 1) &&
            (!topFilters.techniques || topFilters.techniques.length < 1) &&
            (!topFilters.appearances || topFilters.appearances.length < 1) &&
            (!topFilters.textures || topFilters.textures.length < 1) &&
            (!topFilters.types || topFilters.types.length < 1),
        }}
        resultsCount={
          paginationState.kind === 'loaded' ? paginationState.value : undefined
        }
        modalState={state.modalState}
        setModalState={(modalState: ModalState) =>
          setState(topFiltersStateUpdaters.modalStateUpdater(modalState))
        }
        saveAll={{
          url: filteredUrl({
            term: term,
            searchTab: searchTab,
            categoriesFilter: categoriesFilter,
            topFilters: {
              allergens: state.allergens.selected.toArray(),
              techniques: state.techniques.selected.toArray(),
              appearances: state.appearances.selected.toArray(),
              textures: state.textures.selected.toArray(),
              types: state.types.selected.toArray(),
            },
          }),
          current:
            state.allergens.selected.equals(Set(topFilters.allergens)) &&
            state.techniques.selected.equals(Set(topFilters.techniques)) &&
            state.appearances.selected.equals(Set(topFilters.appearances)) &&
            state.textures.selected.equals(Set(topFilters.textures)) &&
            state.types.selected.equals(Set(topFilters.types)),
        }}
      />
    ))

const toTopFilter = (
  categoriesFilter: CategoriesFilter,
  filters: Filter[],
  filterState: TopFilterState,
  currentFilter: string[] = [],
  toTopFilters: (items: string[]) => TopFilters,
  setState: (updater: Updater<TopFiltersState>) => void,
  updater: (updater: Updater<TopFilterState>) => Updater<TopFiltersState>,
  filteredUrl: FilteredUrl,
  term?: string,
  searchTab?: SearchTab
) => ({
  items: filters.map((filter) => ({
    label: filter,
    isChecked: filterState.selected.contains(filter.Title),
    onCheck: () =>
      setState(
        updater(topFiltersStateUpdaters.singleSelectedUpdater(filter.Title))
      ),
  })),
  filterCount: currentFilter.length,
  toggled: filterState.toggled,
  toggle: () => setState(updater(topFiltersStateUpdaters.toggledUpdater)),
  reset: {
    disable: filterState.selected.count() < 1,
    setState: () =>
      setState(updater(topFiltersStateUpdaters.emptySelectedUpdater)),
  },
  save: {
    url: filteredUrl({
      term: term,
      searchTab: searchTab,
      categoriesFilter: categoriesFilter,
      topFilters: toTopFilters(filterState.selected.toArray()),
    }),
    current: filterState.selected.equals(Set(currentFilter)),
  },
})
